import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ChevronLeft, ChevronRight, RefreshCw, FileText } from 'lucide-react';
import { apiGet } from '../../lib/api.js';
import { Card, LoadingPanel, EmptyPanel } from './_shared.jsx';

const PAGE_SIZE = 50;

export default function AuditLogTab() {
  const [logs, setLogs] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState({ action: '', actor: '' });
  const [busy, setBusy] = useState(false);

  function load(o = offset, f = filter) {
    setBusy(true);
    const params = new URLSearchParams({ limit: PAGE_SIZE, offset: o });
    if (f.action) params.set('action', f.action);
    if (f.actor) params.set('actor', f.actor);
    apiGet(`/settings/audit-log?${params.toString()}`).then((r) => {
      if (r?.ok) {
        setLogs(r.logs || []);
        setTotal(r.total || 0);
      }
    }).catch((e) => toast.error(e.message || 'Failed to load audit log')).finally(() => setBusy(false));
  }

  useEffect(() => { load(0, filter); }, []);

  function applyFilter() {
    setOffset(0);
    load(0, filter);
  }

  function page(next) {
    const o = Math.max(0, offset + (next ? PAGE_SIZE : -PAGE_SIZE));
    setOffset(o);
    load(o, filter);
  }

  if (logs === null) return <LoadingPanel label="Loading audit log…" />;

  return (
    <div className="space-y-4">
      <Card
        title="Audit Log"
        description="Last 50 actions per page. Tenant-scoped — only entries for this tenant are visible."
        actions={
          <button onClick={() => load(offset, filter)} disabled={busy} className="btn-ghost text-sm inline-flex items-center gap-2 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /> Refresh
          </button>
        }
      >
        <div className="flex flex-wrap gap-2 mb-2">
          <input
            className="input flex-1 min-w-[200px]"
            placeholder="Filter by action (e.g. settings.tenant.update)"
            value={filter.action}
            onChange={(e) => setFilter({ ...filter, action: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && applyFilter()}
          />
          <input
            className="input flex-1 min-w-[200px]"
            placeholder="Filter by actor (Agent _id)"
            value={filter.actor}
            onChange={(e) => setFilter({ ...filter, actor: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && applyFilter()}
          />
          <button onClick={applyFilter} className="btn-primary text-sm">Apply</button>
        </div>

        {logs.length === 0 ? (
          <EmptyPanel icon={FileText} title="No matching entries" hint="Try clearing filters or check back after some activity." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-white/55 text-xs uppercase tracking-wider">
                <tr className="border-b border-civix-border/40">
                  <th className="text-left py-2 px-2">When</th>
                  <th className="text-left">Action</th>
                  <th className="text-left">Actor</th>
                  <th className="text-left">Target</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l._id} className="border-t border-civix-border/30 hover:bg-white/2">
                    <td className="py-2 px-2 text-white/65 whitespace-nowrap">{new Date(l.createdAt).toLocaleString()}</td>
                    <td className="font-mono text-xs text-civix-purpleSoft">{l.action}</td>
                    <td className="text-white/75">{l.actor?.name || l.actorName || '—'}</td>
                    <td className="text-white/55">{l.target || (l.targetId ? String(l.targetId) : '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-white/50 pt-2 border-t border-civix-border/40">
          <div>
            Showing {offset + 1}–{Math.min(offset + logs.length, total)} of {total}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => page(false)} disabled={offset === 0 || busy} className="btn-ghost text-xs px-2 py-1 disabled:opacity-30">
              <ChevronLeft className="w-3 h-3" />
            </button>
            <button onClick={() => page(true)} disabled={offset + PAGE_SIZE >= total || busy} className="btn-ghost text-xs px-2 py-1 disabled:opacity-30">
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}
